// TextareaField — canonical `textarea` field primitive (comp-ε GAP-3b-i,
// CHG-2433). Multi-line free text (descriptions, notes). Storage: string.
// For formatted content use `rich_text` (RichTextField) instead.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('textarea') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the textarea via FieldShell <label htmlFor=id>;
// validation error in a role="alert" live region; aria-invalid + aria-required
// on the textarea; aria-describedby links the textarea to its error + helper
// text nodes; native <textarea> focus order (no custom focus management needed).

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface TextareaFieldProps extends FieldPrimitiveProps<string> {
  placeholder?: string;
  rows?: number;
  maxLength?: number;
}

export function TextareaField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  placeholder,
  rows = 4,
  maxLength,
}: TextareaFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      <textarea
        id={ids.inputId}
        name={name}
        rows={rows}
        value={value ?? ""}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={{
          ...fieldControlStyle(Boolean(error)),
          height: "auto",
          minHeight: 80,
          padding: "8px 12px",
          resize: "vertical",
          lineHeight: 1.5,
          fontFamily: "inherit",
        }}
      />
    </FieldShell>
  );
}
